import { Component, Inject, OnDestroy, Optional, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ProdutoListaRefinamentoEnum } from '../../../../../../shared/enums/produto/produto-lista-refinamento.enum';
import { AlertService, AlertType } from '../../../../../../shared/services/alert.service';
import { LookupService } from '../../../../../../shared/services/apis/produto/produto-lookup.service';
import { ControleEstoqueLocalStruct } from '../../../../../../shared/services/structs/produto/controle-estoque-local.struct';
import { ProdutoListStruct } from '../../../../../../shared/services/structs/produto/produto-list.struct';
import { CodigoCompostoResolvidoStruct } from '../../../../../../shared/services/structs/produto/codigo-composto-resolvido.struct';
import { InventarioScannerMobileComponent } from '../../../../../../shared/components/inventario-scanner-mobile/inventario-scanner-mobile.component';
import { AbntPipe } from '../../../../../../shared/pipes/abnt.pipe';
import { resolverPrecoDoProduto, textoVigencia } from '../venda-item.preco';

/** Quanto o leitor espera, depois de um código que não resolveu, antes de voltar a ler (a lista fica visível nesse tempo). */
const ATRASO_PARA_RETOMAR_LEITOR_MS = 1500;

/** O que o diálogo devolve ao documento quando o usuário confirma. */
export interface ProdutoEscolhido {
    produto: ProdutoListStruct;
    descricaoComplementar: string | null;
    /** Preenchido quando o produto veio de um CÓDIGO lido (SKU, GTIN/EAN ou EAN + lote/série), não de escolha na lista. */
    lidoPorCodigo?: boolean;
    /** Lote/série embutido no código lido, para a linha nascer com ele (§6.2.1). */
    codigoComposto?: CodigoCompostoResolvidoStruct | null;
}

/** O que o documento passa ao diálogo. Opcional: sem `data` a busca é a geral, sem filtro de depósito no saldo. */
export interface VdProdutoBuscaDialogData {
    /** Depósito que filtra o saldo consultado, quando a regra de estoque é "depósito selecionado" (§6.2.1). */
    idLocalizacaoEstoqueParaSaldo: number | null;
    /** Documento já gravado: a reserva dele volta ao saldo disponível consultado (T21). */
    idVendaReserva: number | null;
    /** Lista do cabeçalho e "Priorizar menor preço": a coluna Preço resolve a lista como a linha faria (§6.2.2.3). */
    idListaCabecalho: number | null;
    priorizarMenorPreco: boolean;
}


/**
 * Busca avançada de produtos (§6.2.1).
 *
 * O campo Item da linha resolve o caso de quem já sabe o que quer. Este diálogo existe para o
 * outro: mostra preço, saldo e saldo disponível lado a lado, e deixa abrir o detalhe do produto
 * — com o estoque por localização — antes de decidir.
 *
 * Busca no MESMO endpoint do campo Item, com os mesmos filtros de quem pode ser vendido: fora o
 * produto-pai de variação e fora quem está com "Permitir inclusão nas vendas" desligado. Duas
 * listas de produto com universos diferentes na mesma tela seria pior do que não ter a segunda.
 */
@Component({
    selector: 'app-vd-produto-busca-dialog',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatButtonModule,
        MatDialogModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatProgressSpinnerModule,
        MatTooltipModule,
        InventarioScannerMobileComponent,
    ],
    templateUrl: './vd-produto-busca-dialog.component.html',
    styleUrl: './vd-produto-busca-dialog.component.css',
})
export class VdProdutoBuscaDialogComponent implements OnDestroy {
    public readonly termo = new FormControl<string>('', { nonNullable: true });

    /**
     * O campo Pesquisar É o campo de bipagem (§6.2.1): o leitor se comporta como teclado — preenche
     * e envia Enter — e mesmo sem o Enter a busca dispara sozinha depois de uma pausa na digitação.
     * Código que resolve exatamente em um produto sai direto para a grade; o resto vira busca.
     */
    private assinaturaDoTermo?: Subscription;
    private ultimoTermoPesquisado: string | null = null;

    /** Leitor de câmera (mobile): o MESMO componente do inventário de estoque. */
    public scannerAberto: boolean = false;
    @ViewChild(InventarioScannerMobileComponent) private scanner?: InventarioScannerMobileComponent;

    public buscando: boolean = false;
    public pesquisou: boolean = false;
    public resultados: ProdutoListStruct[] = [];

    public selecionado: ProdutoListStruct | null = null;

    /** O produto cujo detalhe está aberto. Nulo = a lista está visível. */
    public detalheDe: ProdutoListStruct | null = null;
    public readonly descricaoComplementar = new FormControl<string>('', { nonNullable: true });

    /** Saldo por localização, em cache por produto: o mesmo dado serve à lista e ao detalhe. */
    private estoquesPorProduto = new Map<number, ControleEstoqueLocalStruct[]>();
    public carregandoEstoques: boolean = false;

    /** Quantos resultados por página — o mesmo tamanho que a API usa. */
    public readonly tamanhoDaPagina: number = 20;

    /** A última página JÁ carregada. As anteriores continuam na lista, que é acumulada. */
    private paginaAtual: number = 0;

    /** Quantos existem no total, para o "Ver mais" saber se ainda há o que buscar. */
    public totalDeResultados: number = 0;

    public carregandoMais: boolean = false;

    /**
     * O refino da busca — as MESMAS opções da lista de produtos (Cadastros >> Produtos), por
     * decisão da PO: quem já sabe procurar num lugar não precisa reaprender no outro.
     *
     * `null` = não refinar, e aí a API procura em descrição, SKU e GTIN/EAN ao mesmo tempo.
     */
    public filtroRefinar: number | null = null;

    public readonly refinamentoEnum = ProdutoListaRefinamentoEnum;

    constructor(
        private dialogRef: MatDialogRef<VdProdutoBuscaDialogComponent, ProdutoEscolhido | undefined>,
        private lookupService: LookupService,
        private alertService: AlertService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: VdProdutoBuscaDialogData | null,
    ) {
        this.assinaturaDoTermo = this.termo.valueChanges
            .pipe(debounceTime(450), distinctUntilChanged())
            .subscribe((valor) => {
                const termo = (valor ?? '').trim();

                // Pausa na digitação com um termo novo: busca sozinha, como o leitor faz.
                if (termo && termo !== this.ultimoTermoPesquisado) this.pesquisar();
            });
    }

    public ngOnDestroy(): void {
        this.assinaturaDoTermo?.unsubscribe();
    }

    private get depositoParaSaldo(): number | null {
        return this.data?.idLocalizacaoEstoqueParaSaldo ?? null;
    }

    public get temMais(): boolean {
        return this.resultados.length < this.totalDeResultados;
    }

    /** "Ver mais 5 de 37" — o quanto vem agora, e o quanto ainda falta depois disso. */
    public get rotuloVerMais(): string {
        const faltam = this.totalDeResultados - this.resultados.length;

        return `Ver mais ${Math.min(this.tamanhoDaPagina, faltam)} de ${faltam}`;
    }

    /** O que a busca vai olhar, dito em texto — o menu de refino é discreto demais sozinho. */
    public get subtitulo(): string {
        switch (this.filtroRefinar) {
            case ProdutoListaRefinamentoEnum.CodigoSku: return 'Refinando por código';
            case ProdutoListaRefinamentoEnum.CodigoFornecedor: return 'Refinando por código do fornecedor';
            case ProdutoListaRefinamentoEnum.GtinEan: return 'Refinando por GTIN/EAN';
            case ProdutoListaRefinamentoEnum.Descricao: return 'Refinando por descrição';
            case ProdutoListaRefinamentoEnum.PalavrasChave: return 'Refinando por palavras-chave';
            default: return 'Busca em descrição, código (SKU) e GTIN/EAN';
        }
    }

    /** Pesquisa do zero: volta para a primeira página e descarta o que estava na tela. */
    public pesquisar(): void {
        const termo = this.termo.value?.trim() || '';

        this.ultimoTermoPesquisado = termo;
        this.paginaAtual = 0;
        this.resultados = [];
        this.selecionado = null;
        this.pesquisou = true;

        // Sem termo é a lista geral. Com termo, primeiro a correspondência EXATA de código: um
        // produto sai direto, sem lista (§6.2.1); nada → a busca por conteúdo de sempre.
        if (!termo) {
            this.buscar(true);
            return;
        }

        this.buscando = true;
        this.lookupService.resolverCodigoVenda(termo, true, this.depositoParaSaldo, this.data?.idVendaReserva ?? null).subscribe({
            next: (res) => {
                if (!res.isError && res.produto) {
                    this.buscando = false;
                    this.scanner?.pausar();
                    this.dialogRef.close({
                        produto: res.produto,
                        descricaoComplementar: null,
                        lidoPorCodigo: true,
                        codigoComposto: res.codigoComposto ?? null,
                    });
                    return;
                }

                this.buscar(true);
            },
            error: () => this.buscar(true),
        });
    }

    // ==================================================================== leitor de câmera (mobile)

    public abrirLeitor(): void {
        this.scannerAberto = true;
    }

    public fecharLeitor(): void {
        this.scannerAberto = false;
    }

    /** Código lido pela câmera: entra no campo e segue o mesmo caminho do teclado. */
    public aoLerCodigo(codigo: string): void {
        this.termo.setValue(codigo, { emitEvent: false });
        this.pesquisar();
        // Não resolveu em produto: a lista de resultados fica visível e o leitor volta a ler.
        setTimeout(() => this.scanner?.retomar(), ATRASO_PARA_RETOMAR_LEITOR_MS);
    }

    /** A próxima página, ACUMULADA na lista — o usuário não perde o que já estava vendo. */
    public verMais(): void {
        this.paginaAtual += 1;

        this.buscar(false);
    }

    private buscar(ehPrimeiraPagina: boolean): void {
        if (ehPrimeiraPagina) {
            this.buscando = true;
        } else {
            this.carregandoMais = true;
        }

        this.lookupService
            // `incluirListasPreco`: o produto sai daqui com as listas vigentes que o alcançam, para
            // a linha nascer pelo preço da lista certa — o mesmo que o campo Item já recebe.
            .buscarProdutosPorTexto(this.termo.value?.trim() || undefined, true, true, this.filtroRefinar, this.paginaAtual, true, true,
                this.depositoParaSaldo,
                this.data?.idVendaReserva ?? null)
            .subscribe({
                next: (res) => {
                    this.buscando = false;
                    this.carregandoMais = false;

                    if (res.isError) {
                        if (ehPrimeiraPagina) this.resultados = [];
                        // A página que falhou não conta, senão o "Ver mais" pularia um pedaço.
                        else this.paginaAtual -= 1;

                        this.alertService.show('Erro', res.errorDescription ?? 'Não foi possível buscar os produtos.', AlertType.error);
                        return;
                    }

                    this.resultados = ehPrimeiraPagina ? (res.list ?? []) : [...this.resultados, ...(res.list ?? [])];
                    this.totalDeResultados = res.fullListSize ?? this.resultados.length;
                },
                error: (err) => {
                    this.buscando = false;
                    this.carregandoMais = false;

                    if (ehPrimeiraPagina) this.resultados = [];
                    else this.paginaAtual -= 1;

                    this.alertService.show('Erro', err.message, AlertType.error);
                },
            });
    }

    public selecionar(produto: ProdutoListStruct): void {
        this.selecionado = this.selecionado?.idProduto === produto.idProduto ? null : produto;
    }

    public abrirDetalhe(produto: ProdutoListStruct): void {
        this.detalheDe = produto;
        this.selecionado = produto;
        this.carregarEstoques(produto);
    }

    /** O somatório das localizações, que o popover mostra como "Todos". */
    public totalDosEstoques(produto: ProdutoListStruct): number {
        return this.estoquesDoProduto(produto).reduce((soma, e) => soma + Number(e.saldoDisponivel ?? 0), 0);
    }

    public voltarParaLista(): void {
        this.detalheDe = null;
    }

    public estoquesDoProduto(produto: ProdutoListStruct | null): ControleEstoqueLocalStruct[] {
        if (!produto) return [];

        return this.estoquesPorProduto.get(produto.idProduto) ?? [];
    }

    /** O preço que a linha teria (§6.2.2.3): a lista resolvida entre as vigentes; sem lista, o preço do cadastro. */
    public precoDe(produto: ProdutoListStruct): number {
        const resolvido = resolverPrecoDoProduto(produto.listasPreco, this.data?.idListaCabecalho ?? null, this.data?.priorizarMenorPreco ?? false);

        return resolvido.lista?.preco ?? produto.preco ?? 0;
    }

    /** Rastreio do preço, como na grade: só quando a lista resolvida não é a esperada (cabeçalho ou padrão). */
    public rastreioDe(produto: ProdutoListStruct): string | null {
        const resolvido = resolverPrecoDoProduto(produto.listasPreco, this.data?.idListaCabecalho ?? null, this.data?.priorizarMenorPreco ?? false);

        return resolvido.origem === 'automatica' ? textoVigencia(resolvido.lista) : null;
    }

    public adicionar(): void {
        if (!this.selecionado) {
            this.alertService.show('Selecione um produto', 'Marque a linha do produto que deve entrar no documento.', AlertType.warning);
            return;
        }

        this.dialogRef.close({
            produto: this.selecionado,
            descricaoComplementar: this.descricaoComplementar.value.trim() || null,
        });
    }

    public fechar(): void {
        this.dialogRef.close();
    }

    public carregarEstoques(produto: ProdutoListStruct): void {
        if (this.estoquesPorProduto.has(produto.idProduto)) return;

        this.carregandoEstoques = true;

        this.lookupService.getProdutoSaldoLocal(produto.idProduto).subscribe({
            next: (res) => {
                this.carregandoEstoques = false;
                // Sem saldo em localização nenhuma a lista fica vazia — e vazia é a resposta certa,
                // não um erro. Guardar mesmo assim evita repetir a consulta a cada clique.
                this.estoquesPorProduto.set(produto.idProduto, res.isError ? [] : (res.list ?? []));
            },
            error: () => {
                this.carregandoEstoques = false;
                this.estoquesPorProduto.set(produto.idProduto, []);
            },
        });
    }
}
